/**
 * ชนิดข้อมูลของ Outsourcing (P2-8g, D-103) — เขียนตาม Pydantic schema จริงที่
 * เซิร์ฟเวอร์ serve (`apps/api/src/cap_erp_api/modules/outsourcing/schemas.py`,
 * `.../router.py`) ไม่ใช่ตาม `docs/API_CONTRACT.md` เพียงอย่างเดียว — invariant
 * ข้อแรกของ Work Order นี้สั่งให้อ่าน router จริงก่อนเขียน client เพราะเอกสารเคย
 * เอ่ยถึง route ที่ router จริงไม่ได้ลงทะเบียนมาแล้วครั้งหนึ่ง (ดู `lib/packing/types.ts`)
 *
 * โมดูลสิทธิ์ `OUTSOURCING` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System
 * Administration — ประกาศแยกไว้ที่นี่แทนการ import ข้ามโดเมน ด้วยเหตุผลเดียวกับที่
 * `lib/sales/client.ts` ไม่ import จาก `lib/procurement/client.ts`: เป็นคนละไฟล์ที่
 * Work Order คนละใบเป็นเจ้าของ ผูกกันด้วยค่าคงที่ที่บังเอิญเท่ากันกับฝั่งเซิร์ฟเวอร์
 * ไม่ใช่ import เดียวกัน
 */
export const OUTSOURCING_MODULE = 'OUTSOURCING';

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_OUTSOURCE_ORDER_STATUS = 'DRAFT';
export const READY_TO_DISPATCH_OUTSOURCE_ORDER_STATUS = 'READY_TO_DISPATCH';
export const DISPATCHED_OUTSOURCE_ORDER_STATUS = 'DISPATCHED';
export const PARTIALLY_RETURNED_OUTSOURCE_ORDER_STATUS = 'PARTIALLY_RETURNED';
export const RETURNED_OUTSOURCE_ORDER_STATUS = 'RETURNED';
export const CLOSED_OUTSOURCE_ORDER_STATUS = 'CLOSED';

/**
 * หกสถานะของ `outsource_order.status` (`modules/outsourcing/models.py`,
 * `OutsourceOrderStatus`) — `DRAFT` → `READY_TO_DISPATCH` → `DISPATCHED` →
 * `PARTIALLY_RETURNED`/`RETURNED` → `CLOSED` นอกเส้นทาง
 *
 * หน้าจอนี้ขับได้สี่ทาง: `add line` (คง `DRAFT`), `ready` (`DRAFT` →
 * `READY_TO_DISPATCH`), `dispatch` (`READY_TO_DISPATCH` → `DISPATCHED`) และ
 * `receive` (`DISPATCHED`/`PARTIALLY_RETURNED` → `PARTIALLY_RETURNED`/`RETURNED`)
 * — ไม่มีเส้นทางไป `CLOSED` เลย: การปิดใบเป็นการตัดสินใจแยกที่ไม่ใช่งานของ
 * Work Order นี้ (router docstring: "a route that drifted into it would close
 * documents nobody decided to close")
 */
export const OUTSOURCE_ORDER_STATUSES = [
  DRAFT_OUTSOURCE_ORDER_STATUS,
  READY_TO_DISPATCH_OUTSOURCE_ORDER_STATUS,
  DISPATCHED_OUTSOURCE_ORDER_STATUS,
  PARTIALLY_RETURNED_OUTSOURCE_ORDER_STATUS,
  RETURNED_OUTSOURCE_ORDER_STATUS,
  CLOSED_OUTSOURCE_ORDER_STATUS,
] as const;

export type OutsourceOrderStatus = (typeof OUTSOURCE_ORDER_STATUSES)[number];

/** สามสถานะของ `outsource_return_discrepancy.status` (`OutsourceDiscrepancyStatus`) */
export const OPEN_DISCREPANCY_STATUS = 'OPEN';
export const PENDING_MANAGEMENT_APPROVAL_DISCREPANCY_STATUS = 'PENDING_MANAGEMENT_APPROVAL';
export const APPROVED_DISCREPANCY_STATUS = 'APPROVED';

/** สามสถานะคุณภาพที่ D-103/D-059/D-086 กำหนดให้ล็อตที่กลับมาลงได้ */
export const RELEASED_QUALITY_STATUS = 'RELEASED';
export const QUALITY_HOLD_QUALITY_STATUS = 'QUALITY_HOLD';

/**
 * ตัวเลือกของ "หมายเหตุสถานะคุณภาพ" ที่ผู้ส่งออกกรอกได้ต่อแถวในฟอร์ม dispatch —
 * **ข้อมูลอ้างอิงของหน้าจอเท่านั้น ไม่ได้ส่งไปกับ request** (`OutsourceDispatchPackageRequest`
 * ไม่มีฟิลด์นี้เลยและ `extra="forbid"` จะตอบ 400 ถ้าส่งไป)
 *
 * มีไว้เพราะ invariant ของ Work Order นี้ระบุตรง ๆ ว่า "A LOT UNDER ANY QUALITY
 * STATUS MAY BE SENT OUT" — `QUALITY_HOLD`, `BLOCKED` และ `REJECTED` ส่งออกได้
 * เหมือนกันหมด **ตรงข้ามกับหน้าจอแพ็กสินค้าที่ส่งได้เฉพาะ `RELEASED`** — ถ้าหน้าจอ
 * นี้จะโชว์คำเตือนคุณภาพเลย มันต้องไม่ปิดกั้นอะไรเลย (ดู `outsourcingView.ts`
 * `dispatchPackageQualityNote`)
 */
export const DISPATCH_LOT_QUALITY_HINTS = [
  '',
  'RELEASED',
  'QUALITY_HOLD',
  'BLOCKED',
  'REJECTED',
] as const;

export type DispatchLotQualityHint = (typeof DISPATCH_LOT_QUALITY_HINTS)[number];

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */
/* ค่าเดียวกับที่ `schemas.py` ประกาศ — restated ที่นี่ด้วยเหตุผลเดียวกับที่
 * `modules/outsourcing/schemas.py` restate ความยาวจากโดเมนอื่นแทนการ import ข้าม
 * โมดูล: ไฟล์นี้ไม่ import จาก `lib/` โดเมนอื่นเลย */

/** `plant_code` ≤10 (`master_data/models.py PLANT_CODE_LENGTH`) */
export const PLANT_CODE_MAX_LENGTH = 10;
/** `supplier_code` ≤20 (`schemas.py SUPPLIER_CODE_LENGTH`) */
export const SUPPLIER_CODE_MAX_LENGTH = 20;
/** `process_code` ≤20 (`master_data/outsource.py OUTSOURCE_PROCESS_CODE_LENGTH`) */
export const OUTSOURCE_PROCESS_CODE_MAX_LENGTH = 20;
/** `order_no` (ใบสั่งผลิตต้นทาง) ≤20 (`schemas.py PRODUCTION_ORDER_NO_LENGTH`) */
export const PRODUCTION_ORDER_NO_MAX_LENGTH = 20;
/** `storage_location_code` ≤20 (`schemas.py STORAGE_LOCATION_CODE_LENGTH`) */
export const STORAGE_LOCATION_CODE_MAX_LENGTH = 20;
/** `uom_code` ≤10 (`master_data/product.py UOM_CODE_LENGTH`) */
export const UOM_CODE_MAX_LENGTH = 10;
/** `note`/`variance_reason` ≤255 (`models.py NOTE_LENGTH`) */
export const NOTE_MAX_LENGTH = 255;
/** `tag_code` ≤40 (`models.py TAG_CODE_LENGTH`) */
export const TAG_CODE_MAX_LENGTH = 40;

/* ------------------------------------------------------------- กฎธุรกิจ --- */
/* รหัสกฎที่ `service.py` ตั้งชื่อไว้ — เรียงตามลำดับ create → add-line → ready →
 * dispatch → receive → approve-discrepancy */

export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';
export const SUPPLIER_NOT_FOUND = 'SUPPLIER_NOT_FOUND';
export const SUPPLIER_IS_BLOCKED = 'SUPPLIER_IS_BLOCKED';
export const OUTSOURCE_PROCESS_NOT_FOUND = 'OUTSOURCE_PROCESS_NOT_FOUND';
export const OUTSOURCE_PROCESS_NOT_ACTIVE = 'OUTSOURCE_PROCESS_NOT_ACTIVE';

export const UOM_NOT_FOUND = 'UOM_NOT_FOUND';
export const PRODUCTION_ORDER_NOT_FOUND = 'PRODUCTION_ORDER_NOT_FOUND';
export const LINE_PLANT_MUST_MATCH_ORDER = 'LINE_PLANT_MUST_MATCH_ORDER';
export const OPERATION_NOT_FOUND = 'OPERATION_NOT_FOUND';
export const OPERATION_ALREADY_ON_THIS_ORDER = 'OPERATION_ALREADY_ON_THIS_ORDER';
export const LINE_NO_ALREADY_TAKEN = 'LINE_NO_ALREADY_TAKEN';
export const QUANTITY_MUST_BE_POSITIVE = 'QUANTITY_MUST_BE_POSITIVE';

export const OUTSOURCE_ORDER_HAS_NO_LINES = 'OUTSOURCE_ORDER_HAS_NO_LINES';
export const READY_BY_MUST_DIFFER_FROM_CREATOR = 'READY_BY_MUST_DIFFER_FROM_CREATOR';
export const OUTSOURCE_ORDER_TRANSITION_NOT_ALLOWED = 'OUTSOURCE_ORDER_TRANSITION_NOT_ALLOWED';

export const OUTSOURCE_ORDER_NOT_READY_TO_DISPATCH = 'OUTSOURCE_ORDER_NOT_READY_TO_DISPATCH';
export const DISPATCH_HAS_NO_PACKAGES = 'DISPATCH_HAS_NO_PACKAGES';
export const STORAGE_LOCATION_NOT_FOUND = 'STORAGE_LOCATION_NOT_FOUND';
export const DESTINATION_MUST_BE_A_SUBCONTRACTOR_LOCATION =
  'DESTINATION_MUST_BE_A_SUBCONTRACTOR_LOCATION';
export const DESTINATION_SUPPLIER_MUST_MATCH_ORDER = 'DESTINATION_SUPPLIER_MUST_MATCH_ORDER';
export const NO_ACCOUNT_MAPPING = 'NO_ACCOUNT_MAPPING';
export const LOT_NOT_FOUND = 'LOT_NOT_FOUND';
export const LOT_PLANT_MUST_MATCH_ORDER = 'LOT_PLANT_MUST_MATCH_ORDER';
export const LINE_NOT_FOUND = 'LINE_NOT_FOUND';
export const SOURCE_MUST_DIFFER_FROM_DESTINATION = 'SOURCE_MUST_DIFFER_FROM_DESTINATION';
export const TAG_QUANTITY_EXCEEDS_LINE = 'TAG_QUANTITY_EXCEEDS_LINE';
export const INSUFFICIENT_STOCK_AT_SOURCE = 'INSUFFICIENT_STOCK_AT_SOURCE';

export const OUTSOURCE_ORDER_NOT_DISPATCHED = 'OUTSOURCE_ORDER_NOT_DISPATCHED';
export const RETURN_HAS_NO_PACKAGES = 'RETURN_HAS_NO_PACKAGES';
export const RECEIVING_LOCATION_MUST_NOT_BE_A_SUBCONTRACTOR_LOCATION =
  'RECEIVING_LOCATION_MUST_NOT_BE_A_SUBCONTRACTOR_LOCATION';
export const QUANTITY_MUST_NOT_BE_NEGATIVE = 'QUANTITY_MUST_NOT_BE_NEGATIVE';
export const TAG_NOT_ON_THIS_ORDER = 'TAG_NOT_ON_THIS_ORDER';
export const TAG_ALREADY_SCANNED_ON_THIS_RETURN = 'TAG_ALREADY_SCANNED_ON_THIS_RETURN';
export const RETURN_LINE_RECORDS_NOTHING = 'RETURN_LINE_RECORDS_NOTHING';
export const VARIANCE_NEEDS_A_REASON = 'VARIANCE_NEEDS_A_REASON';
export const REASON_WITHOUT_A_VARIANCE = 'REASON_WITHOUT_A_VARIANCE';
export const RETURN_EXCEEDS_TAG = 'RETURN_EXCEEDS_TAG';
export const NO_UNIT_COST = 'NO_UNIT_COST';
export const IDEMPOTENCY_KEY_REQUIRED = 'IDEMPOTENCY_KEY_REQUIRED';
export const IDEMPOTENCY_KEY_REUSED = 'IDEMPOTENCY_KEY_REUSED';

export const APPROVAL_AUTHORITY_BELOW_MANAGER = 'APPROVAL_AUTHORITY_BELOW_MANAGER';
export const OUTSOURCE_RETURN_DISCREPANCY_NOT_APPROVABLE =
  'OUTSOURCE_RETURN_DISCREPANCY_NOT_APPROVABLE';
export const APPROVER_MUST_DIFFER_FROM_RAISER = 'APPROVER_MUST_DIFFER_FROM_RAISER';

export const OUTSOURCE_ORDER_FIELD_IS_SERVER_OWNED = 'OUTSOURCE_ORDER_FIELD_IS_SERVER_OWNED';

/* ------------------------------------------------------------- resource --- */

export interface OutsourceOrderLine {
  readonly line_no: number;
  readonly production_order_id: string;
  readonly order_no: string;
  readonly operation_seq: number;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly quantity: string;
  readonly uom_code: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * ใบสั่งจ้างผลิตภายนอกหนึ่งใบ **พร้อมบรรทัดครบ** — รูปของ `GET`, `POST`, `ready`,
 * และเป็นสมาชิกของ `OutsourceDispatchResult`/`OutsourceReturnResult` ด้วย
 *
 * ทุกฟิลด์ตรงตาม `OutsourceOrderResponse` ใน `schemas.py` ตรง ๆ — ฟิลด์ที่เป็น
 * `Optional[...] = None` เท่านั้นที่ nullable ที่นี่ ส่วนที่เหลือ required เสมอ
 * เพราะไฟล์นี้มีซอร์สของเซิร์ฟเวอร์ให้อ่านจริง
 */
export interface OutsourceOrder {
  readonly outsource_order_id: string;
  readonly outsource_order_no: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly supplier_id: string;
  readonly supplier_code: string;
  readonly outsource_process_id: string;
  readonly process_code: string;
  readonly expected_return_date: string;
  readonly status: string;
  readonly created_by_employee_id: string;
  readonly ready_by_employee_id: string | null;
  readonly ready_at: string | null;
  readonly dispatched_by_employee_id: string | null;
  readonly dispatched_at: string | null;
  readonly note: string | null;
  readonly lines: readonly OutsourceOrderLine[];
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/outsource-orders` — envelope มาตรฐานของทุกกลุ่ม */
export interface OutsourceOrderListPage {
  readonly items: readonly OutsourceOrder[];
  readonly next_cursor: string | null;
}

/** หนึ่ง `inv_ledger` row ที่การ dispatch/receive โพสต์ — สัญลักษณ์บวก/ลบมาจริง */
export interface OutsourceMovement {
  readonly ledger_id: string;
  readonly storage_location_id: string;
  readonly storage_location_code: string;
  readonly lot_id: string;
  /** สัญลักษณ์จริง — ลบที่ต้นทาง บวกที่ปลายทาง */
  readonly qty: string;
  readonly movement_type: string;
  readonly reference_doc_type: string;
  readonly reference_doc_id: string;
}

export interface OutsourceTag {
  readonly tag_code: string;
  readonly line_no: number;
  readonly lot_id: string;
  readonly source_location_code: string;
  readonly quantity: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * ผลของการ dispatch — **THE NOT-A-SALE HEADLINE**: `movements` มีทั้งแถวลบที่
 * ต้นทางและแถวบวกที่ `destination_location_code` เสมอ เพราะการส่งออกคือการย้าย
 * ระหว่างที่เก็บของโรงงานเดียวกัน ไม่ใช่การขาย — ไม่มีเอกสารบัญชีถูกสร้างเลย
 * (D-103 ข้อ 1)
 */
export interface OutsourceDispatchResult {
  readonly order: OutsourceOrder;
  readonly destination_location_id: string;
  readonly destination_location_code: string;
  readonly tags: readonly OutsourceTag[];
  readonly movements: readonly OutsourceMovement[];
}

/**
 * ส่วนต่างที่การรับกลับแบบขาดบันทึกไว้ (D-103 ข้อ 9/10g) — **ไม่มี `GET` แยก**
 * ในทั้ง contract: router docstring ระบุว่า id ที่ต้องใช้อนุมัติมาจากคำตอบของการ
 * รับกลับโดยตรงเท่านั้น หน้าจอนี้จึงเก็บมันไว้ใน state ของผลการรับกลับล่าสุด
 * ไม่ใช่ดึงจาก endpoint แยก
 */
export interface OutsourceReturnDiscrepancy {
  readonly discrepancy_id: string;
  readonly discrepancy_type: string;
  readonly status: string;
  readonly expected_qty: string;
  readonly actual_qty: string;
  readonly raised_by_employee_id: string;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly reason: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * หนึ่งแพ็กเกจที่กลับมา — **THE SHORTAGE HEADLINE**: `discrepancy` เป็น `null`
 * เมื่อ `variance_quantity` เป็นศูนย์ (รับครบ, ไม่มีผลทางบัญชี) และไม่ใช่ `null`
 * เมื่อขาด (เขียนเป็นค่าใช้จ่ายไปแล้วในธุรกรรมเดียวกับการรับกลับนี้) — สองกรณีนี้
 * ต้องแสดง**ต่างกัน**บนหน้าจอ (D-103 ข้อ 10g)
 */
export interface OutsourceReturnLine {
  readonly line_no: number;
  readonly tag_code: string;
  readonly dispatched_lot_id: string;
  readonly returned_lot_id: string | null;
  readonly dispatched_quantity: string;
  readonly returned_quantity: string;
  readonly variance_quantity: string;
  readonly variance_reason: string | null;
  readonly quality_status: string | null;
  readonly discrepancy: OutsourceReturnDiscrepancy | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface OutsourceReturnResult {
  readonly outsource_return_id: string;
  readonly outsource_return_no: string;
  readonly order: OutsourceOrder;
  readonly receiving_location_id: string;
  readonly receiving_location_code: string;
  readonly subcontractor_location_id: string;
  readonly subcontractor_location_code: string;
  readonly received_by_employee_id: string;
  readonly received_at: string;
  readonly note: string | null;
  readonly lines: readonly OutsourceReturnLine[];
  readonly movements: readonly OutsourceMovement[];
  readonly created_at: string;
  readonly updated_at: string;
}

/* -------------------------------------------------------------- requests -- */

/** body ของ `POST /v1/outsource-orders` — เปิดใบเสมอที่ `DRAFT`, ไม่มีบรรทัด */
export interface CreateOutsourceOrderBody {
  readonly plant_code: string;
  readonly supplier_code: string;
  readonly process_code: string;
  /** ISO-8601 พร้อม offset — แปลงจากช่อง `<input type="date">` ด้วย `businessDayToIso` */
  readonly expected_return_date: string;
  readonly note?: string;
}

/** body ของ `POST /v1/outsource-orders/{id}/lines` — หนึ่งเรียกหนึ่งบรรทัด */
export interface AddOutsourceOrderLineBody {
  readonly order_no: string;
  readonly operation_seq: number;
  /** สตริงเสมอ — `> 0` */
  readonly quantity: string;
  readonly uom_code: string;
}

/** หนึ่งแพ็กเกจในฟอร์ม dispatch */
export interface DispatchPackageInput {
  readonly line_no: number;
  readonly lot_id: string;
  readonly source_location_code: string;
  /** สตริงเสมอ — `> 0` */
  readonly quantity: string;
}

/** body ของ `POST /v1/outsource-orders/{id}/dispatch` */
export interface DispatchOutsourceOrderBody {
  readonly destination_location_code: string;
  readonly packages: readonly DispatchPackageInput[];
}

/** หนึ่งแพ็กเกจที่สแกนในฟอร์มรับกลับ */
export interface ReturnPackageInput {
  readonly tag_code: string;
  /** สตริงเสมอ — `>= 0` */
  readonly returned_quantity: string;
  /** ไม่ระบุ = ศูนย์ ("ยังไม่มาส่วนที่เหลือ") — สตริงเสมอเมื่อระบุ */
  readonly variance_quantity?: string;
  readonly variance_reason?: string;
}

/** body ของ `POST /v1/outsource-orders/{id}/receive` — ต้องมี `Idempotency-Key` */
export interface ReceiveOutsourceReturnBody {
  readonly receiving_location_code: string;
  readonly packages: readonly ReturnPackageInput[];
  readonly note?: string;
}
